/// <amd-module name="WidgetCommon/WorkItemResultsGrid/WorkItemResultsGrid" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";

import { WorkItemResultsDataProvider } from "WidgetCommon/WorkItemResultsGrid/WorkItemResultsDataProvider";
import { WorkItemTypeFilters } from "WidgetCommon/WorkItemResultsGrid/WorkItemTypeFilters";

import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IWorkItemTrackingDataProvider } from "Wit/UI/Data";
import { IWorkItemTreeGridColumn, WorkItemTreeGrid } from "Wit/UI/WorkItemTreeGrid";
import { WorkItemSmallView } from "Wit/UI/WorkItemViews";

import { IWidgetTelemetryService } from "Dashboards/WidgetCommon/WidgetTelemetry";

import { NoDataMessage } from "Reporting/Visuals/VisualMessages/NoDataMessage";
import { VisualTitle } from "Reporting/Visuals/VisualTitle";

import { OpenWorkItemBehavior, WorkItemResultsGridProps } from "./WorkItemResultsGrid.Props";

import { TimerManagement } from "VSS/Core/TimerManagement";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { WorkItemResultsGridUserSettings } from "./WorkItemResultsGridSettings";

interface WorkItemResultsGridState {
    filteredWorkItemIds?: number[];
    columnWidths?: { [key: string]: number };
}

export function getSuggestedOpenWorkItemBehavior(avoidShowingModalUI?: boolean): OpenWorkItemBehavior {
    return avoidShowingModalUI ? OpenWorkItemBehavior.NewTab : OpenWorkItemBehavior.Dialog;
}

export class WorkItemResultsGrid extends VssComponent<WorkItemResultsGridProps, WorkItemResultsGridState> {
    private static gridClassName = "work-item-results-grid";

    private dataProvider: IWorkItemTrackingDataProvider;

    constructor(props: WorkItemResultsGridProps, context: IVssComponentContext) {
        super(props);
        this.state = {};
        this.dataProvider = new WorkItemResultsDataProvider(
            context.pageContext,
            props.projectId,
            props.columns.map((column) => column.referenceName)
        );
    }

    /**
     * Returns table widths for columns. A negative number indicates % of space occupied by the column. Positive number indicates fixed width.
     * @param columnRefName
     */
    public getColumnWidth(columnRefName: string): ObservableValue<number> {
        const { workItemRelations } = this.props;
        let value: ObservableValue<number>;
        if (this.state.columnWidths && this.state.columnWidths[columnRefName]) {
            value = new ObservableValue(this.state.columnWidths[columnRefName]);
        } else {
            switch (columnRefName) {
                case CoreFieldRefNames.Title:
                    value = new ObservableValue(-60);
                    break;
                case CoreFieldRefNames.Id:
                    value = workItemRelations ? new ObservableValue(80) : new ObservableValue(50);
                    break;
                default:
                    value = new ObservableValue(-20);
                    break;
            }
        }
        const saveUserColumnWidthSetting: (value: number) => void = new TimerManagement().debounce<(value: number) => void>(
            (value: number) => {
                this.saveColumnSettings(value, columnRefName);
            },
            1000,
            { leading: false, trailing: true }
        );

        value.subscribe((value: number, action: string) => {
            if (action === "set") {
                saveUserColumnWidthSetting(value);
            }
        });
        return value;
    }

    public render(): JSX.Element | null {
        // If the props ask for columns we haven't made a WIT request for, we need a new data provider to fetch that data
        const prevColumnRefNames = this.dataProvider.getFieldReferenceNames() || [];
        if (
            !this.props.columns.every((column) =>
                prevColumnRefNames.some((prevColumnRefName) => localeIgnoreCaseComparer(prevColumnRefName, column.referenceName) === 0)
            )
        ) {
            this.dataProvider = new WorkItemResultsDataProvider(
                this.context.pageContext,
                this.props.projectId,
                this.props.columns.map((column) => column.referenceName)
            );
        }

        if (!this.state.columnWidths) {
            return null;
        } else if (this.props.itemsSource && this.props.itemsSource.length > 0) {
            return (
                <div className={WorkItemResultsGrid.gridClassName}>
                    {<VisualTitle text={this.props.title} />}
                    {this.renderVisualFilters()}
                    {this.props.children}
                    <WorkItemTreeGrid
                        className={"wit-view-grid"}
                        dataProvider={this.dataProvider}
                        itemsSource={this.state && this.state.filteredWorkItemIds ? this.state.filteredWorkItemIds : this.props.itemsSource}
                        workItemRelations={this.props.workItemRelations}
                        queryType={this.props.queryType}
                        columns={this.getColumns()}
                        pageSize={this.props.pageSize}
                    />
                </div>
            );
        } else {
            return (
                <div className={WorkItemResultsGrid.gridClassName}>
                    {this.props.children}
                    <NoDataMessage visualTitle={this.props.title} size={this.props.size} cardId={this.props.noDataCardId} />
                </div>
            );
        }
    }

    public async componentDidMount() {
        let columnWidths: { [key: string]: number } = {};
        if (this.props.storageId) {
            try {
                columnWidths = await WorkItemResultsGridUserSettings.load(this.context.pageContext, this.props.storageId);
            } catch (error) {
                const widgetTelemetryService = this.context.pageContext.getService<IWidgetTelemetryService>("IWidgetTelemetryService");
                widgetTelemetryService.onWidgetFailure(this.props.widgetPropsForTelemetry, error, "LoadUsewColumnWidthSettings", {});
            }
        }
        this.setState({ columnWidths });
    }

    public updateFilteredWorkItemIds = (filteredWorkItemIds: number[]) => {
        this.setState({ filteredWorkItemIds: filteredWorkItemIds });
    };

    public publishWorkItemTypeFiltersErrorTelemetry = (error: any, operationName: string) => {
        const widgetTelemetryService = this.context.pageContext.getService<IWidgetTelemetryService>("IWidgetTelemetryService");
        widgetTelemetryService.onWidgetFailure(this.props.widgetPropsForTelemetry, error && error.message, operationName, error);
    };

    public renderVisualFilters(): JSX.Element | null {
        return this.props.showVisualFilters ? (
            <WorkItemTypeFilters
                updateFilteredWorkItemIds={this.updateFilteredWorkItemIds}
                itemsSource={this.props.itemsSource}
                projectId={this.props.projectId}
                dataProvider={this.dataProvider}
                width={this.props.size.width}
                publishErrorTelemetry={this.publishWorkItemTypeFiltersErrorTelemetry}
            />
        ) : null;
    }

    private saveColumnSettings(width: number, column: string): void {
        if (this.props.storageId) {
            WorkItemResultsGridUserSettings.save(this.context.pageContext, this.props.storageId, {
                [column]: width
            });
        }
    }

    private getColumns(): IWorkItemTreeGridColumn[] {
        return this.props.columns.map<IWorkItemTreeGridColumn>((column) => {
            const treeColumn: IWorkItemTreeGridColumn = {
                key: column.referenceName,
                fieldName: column.referenceName,
                name: column.name,
                minWidth: 20,
                width: this.getColumnWidth(column.referenceName),
                isResizable: true
            };

            // Use a custom renderer for the title column to display links in a dialog/new tab.
            if (column.referenceName === CoreFieldRefNames.Title) {
                treeColumn.onRender = this.onTitleRender;
            }

            // Use a custom renderer for the parent column to display links in a dialog/new tab.
            if (column.referenceName === CoreFieldRefNames.Parent) {
                treeColumn.onRender = this.onParentItemRender;
            }

            return treeColumn;
        });
    }

    private onTitleRender = (item: WorkItem | null, index?: number | undefined, column?: IWorkItemTreeGridColumn | undefined) => {
        if (item) {
            return (
                <WorkItemSmallView
                    dataProvider={this.dataProvider}
                    data={item}
                    showId={false}
                    showTitleLink={true}
                    onTitleLinkClick={this.onTitleLinkClick}
                />
            );
        }

        return null;
    };

    private onParentItemRender = (item: WorkItem): JSX.Element => {
        if (!item || !item.fields[CoreFieldRefNames.Parent]) {
            return <></>;
        }

        const parentWorkItem = item.fields[CoreFieldRefNames.Parent];

        if (parentWorkItem.fetchError) {
            return (
                <Icon
                    className={"icon-large-margin error-text-important font-weight-semibold"}
                    iconName="AlertSolid"
                    tooltipProps={{ text: parentWorkItem.fetchError }}
                />
            );
        } else {
            return (
                <WorkItemSmallView
                    dataProvider={this.dataProvider}
                    data={parentWorkItem}
                    showId={false}
                    showTitleLink={true}
                    onTitleLinkClick={this.onTitleLinkClick}
                />
            );
        }
    };

    private onTitleLinkClick = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, workItem: WorkItem) => {
        this.activateWorkItem(workItem);
        const widgetTelemetryService = this.context.pageContext.getService<IWidgetTelemetryService>("IWidgetTelemetryService");
        widgetTelemetryService.onWidgetClick(this.props.widgetPropsForTelemetry, {
            linkDescription: `WorkItemId: ${workItem.id}`
        });

        return true; // Prevent default (navigation to the work item page)
    };

    private activateWorkItem(workItem: WorkItem): void {
        const { openWorkItemBehavior } = this.props;

        switch (openWorkItemBehavior) {
            case OpenWorkItemBehavior.Dialog:
                this.openWorkItemDialog(workItem);
                break;
            case OpenWorkItemBehavior.NewTab:
            default:
                this.openWorkItemInNewTab(workItem);
        }
    }

    private openWorkItemDialog(workItem: WorkItem): void {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            return (
                <LegacyComponent
                    modules={["WorkItemTracking/SharedScripts/WorkItemDialogShim"]}
                    wrappedType="WorkItemDialog"
                    wrappedProps={{
                        workItemId: workItem.id,
                        onDismiss
                    }}
                />
            );
        });
    }

    private openWorkItemInNewTab(workItem: WorkItem): void {
        const workItemUrl = workItem._links && workItem._links.html && workItem._links.html.href;
        if (workItemUrl) {
            const newWindow = window.open(workItemUrl, "_blank");
            if (newWindow) {
                newWindow.opener = null;
            }
        }
    }
}

VssComponent.register("workItemResultsGrid", WorkItemResultsGrid);
